// src/utils/apiBase.ts
/**
 * Single source of truth for backend bases.
 * - ADMIN + KOM expose REST under /api/v1
 * - AGENTS usually exposes raw routes (no /api/v1)
 */
export type Service = "admin" | "kom" | "agents";

const isDev = process.env.NODE_ENV === "development";

// ---- Admin API (Auth, Predictions, Admin console)
const ADMIN_DEV =
  process.env.NEXT_PUBLIC_ADMIN_BACKEND_URL_DEV ?? "http://localhost:3002";
const ADMIN_PROD =
  process.env.NEXT_PUBLIC_ADMIN_BACKEND_URL ??
  process.env.ADMIN_BACKEND_URL ??
  "https://prediction-admin-server-35b70fa68ffd.herokuapp.com";

// ---- KOM API (KOM predictor)
const KOM_DEV =
  process.env.NEXT_PUBLIC_KOM_BACKEND_URL_DEV ?? "http://localhost:3003";
const KOM_PROD =
  process.env.NEXT_PUBLIC_KOM_BACKEND_URL ??
  process.env.KOM_BACKEND_URL ??
  "https://phoebe-kom-predictions-f54772ed4775.herokuapp.com";

// ---- Agents API (Interviewer/Frontend Agent)
const AGENTS_DEV =
  process.env.NEXT_PUBLIC_AGENT_BACKEND_URL_DEV ?? "http://127.0.0.1:5001";
const AGENTS_PROD =
  process.env.NEXT_PUBLIC_AGENT_BACKEND_URL ??
  process.env.AGENT_BACKEND_URL ??
  "https://agents.phoebeapp.io";

// Normalize and safely join URLs
const stripTrailing = (s: string) => s.replace(/\/+$/, "");
const ensureLeading = (s: string) => (s.startsWith("/") ? s : `/${s}`);
const join = (base: string, path = "") =>
  `${stripTrailing(base)}${ensureLeading(path)}`;

function baseFor(service: Service): string {
  switch (service) {
    case "admin":
      return isDev ? ADMIN_DEV : ADMIN_PROD;
    case "kom":
      return isDev ? KOM_DEV : KOM_PROD;
    case "agents":
      return isDev ? AGENTS_DEV : AGENTS_PROD;
    default:
      return isDev ? ADMIN_DEV : ADMIN_PROD;
  }
}

/**
 * Returns full API base for a service, including version segment when applicable.
 * - admin, kom => .../api/v1
 * - agents     => raw origin (no version prefix by default)
 */
export function getApiBase(service: Service = "admin", version = "v1"): string {
  const origin = baseFor(service);
  if (service === "agents") return stripTrailing(origin);
  return join(origin, `/api/${version}`);
}

/**
 * Build a full URL for a given service + path.
 * Example:
 *  url("admin", "/admin/predictions") -> https://api.../api/v1/admin/predictions
 */
export function url(
  service: Service,
  path: string,
  version = "v1"
): string {
  const base = getApiBase(service, version);
  return join(base, path);
}